/**
 * Default app-relative URL for the CADS SVG sprite (`symbol-defs.svg`). Host apps should add a build asset entry that
 * copies `libs/angular-cads/src/assets/symbol-defs.svg` to this path (e.g. `output: /assets/cads`).
 */
export const CADS_ICON_SPRITE_DEFAULT_URL = 'assets/cads/symbol-defs.svg';

/**
 * **xlarge** — 88×88px frame (main template / partner header); glyph defaults to 40px unless overridden.
 */
export type CadsIconViewBitSize = 'small' | 'medium' | 'large' | 'xlarge';

/**
 * **dot** — filled status circle only (semantic {@link CadsIconViewBitConfig.color} **base**); no sprite or label.
 * **icon** — transparent frame for row affordances.
 */
export type CadsIconViewBitShape = 'rounded' | 'circle' | 'icon' | 'dot';

/** Sprite glyph size in CSS pixels per {@link CadsIconViewBitSize} (frame uses {@link CADS_ICON_VIEW_BIT_FRAME_PX}). */
export const CADS_ICON_VIEW_BIT_SVG_PX: Readonly<Record<CadsIconViewBitSize, number>> = {
  small: 14,
  medium: 18,
  large: 22,
  xlarge: 40,
};

/** Outer frame width/height in CSS pixels for **rounded** / **circle** (and non-collapsed **icon**). */
export const CADS_ICON_VIEW_BIT_FRAME_PX: Readonly<Record<CadsIconViewBitSize, number>> = {
  small: 32,
  medium: 40,
  large: 48,
  xlarge: 88,
};

/**
 * Configuration for {@link CadsIconViewBitComponent}. When using a sprite glyph, `icon` must match `<symbol id="…">`
 * in `symbol-defs.svg` exactly (e.g. `credit_card`, `bank-circle`). For step-style numerals, set {@link label} and omit
 * `icon` (or leave it empty).
 */
export interface CadsIconViewBitConfig {
  /** Sprite symbol id. Omit when using {@link CadsIconViewBitConfig.label} only. */
  readonly icon?: string;
  /**
   * Centered text instead of a sprite—e.g. step numbers (`"1"`, `"2"`). Use with **circle** (or **rounded**) and a semantic
   * `color`; typically no `icon`.
   */
  readonly label?: string;
  /**
   * Default glyph color when {@link CadsIconViewBitConfig.iconColor} is omitted, and tint input for **rounded** / **circle**.
   * Use `var(--cads-color-{key})`, `var(--cads-color-{key}-deep)`, etc.: the family stem is detected so the frame background
   * uses **`{key}-pastel`** fill and **`{key}`** border (same as info/badge bits). Non-token values fall back to `color-mix` with the surface.
   */
  readonly color?: string;
  /**
   * Optional glyph color (sprite / step label). When omitted, matches `color`—use when the tint is very dark or very
   * light and the symbol needs a contrasting fill.
   */
  readonly iconColor?: string;
  readonly size?: CadsIconViewBitSize;
  /**
   * Sprite glyph size in CSS pixels. When set, overrides the {@link size} preset in {@link CADS_ICON_VIEW_BIT_SVG_PX}.
   * The outer frame still uses the preset frame unless {@link collapsed} is true with {@link shape} **icon**.
   */
  readonly iconSizePx?: number;
  /**
   * When **both** {@link iconWidthPx} and {@link iconHeightPx} are set, the sprite `<svg>` uses those dimensions instead of a
   * square {@link iconSizePx} / size preset (e.g. wide symbols such as `toggle-on` / `toggle-off`, viewBox 72×48).
   */
  readonly iconWidthPx?: number;
  /**
   * @see {@link CadsIconViewBitConfig.iconWidthPx}
   */
  readonly iconHeightPx?: number;
  /**
   * When **true** with {@link shape} **icon**, the outer frame does not reserve a fixed square
   * ({@link CADS_ICON_VIEW_BIT_FRAME_PX}); the box wraps the glyph only—reduces stray space next to row text
   * (e.g. card/tap chevrons). Ignored for **rounded** / **circle**.
   */
  readonly collapsed?: boolean;
  /**
   * **rounded** / **circle**: pastel frame + base border; **icon**: transparent frame; **dot**: colored dot only (base fill,
   * no frame)—use for compact status indicators.
   */
  readonly shape?: CadsIconViewBitShape;
  /** Override sprite URL (fragment `#id` is appended). Default {@link CADS_ICON_SPRITE_DEFAULT_URL}. */
  readonly spriteUrl?: string;
  /** When set, the bit is exposed as an image with this accessible name; otherwise decorative (`aria-hidden`). */
  readonly accessibleLabel?: string;
}
